import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import type { DropResult } from '@hello-pangea/dnd';
import { GripVertical, Save, RefreshCw } from 'lucide-react';
import { useDeleteHolidayType, useHolidayTypes, useReorderHolidayTypes } from '../../../lib/hooks/useHolidayTypes';
import CloudflareImage from '../../../components/ui/CloudflareImage';
import type { HolidayType } from '../../../lib/types/api';
import ErrorModal from '../../../components/ui/ErrorModal';

const HolidayTypesListPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const { data: holidayTypes, isLoading, error } = useHolidayTypes();
  const deleteHolidayType = useDeleteHolidayType();
  const reorderHolidayTypes = useReorderHolidayTypes();
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Local state for draggable items
  const [orderedItems, setOrderedItems] = useState<HolidayType[]>([]);
  const [hasOrderChanged, setHasOrderChanged] = useState(false);

  // Sync with API data when it loads
  useEffect(() => {
    if (holidayTypes) {
      setOrderedItems(holidayTypes);
      setHasOrderChanged(false);
    }
  }, [holidayTypes]);

  const handleDelete = async (id: number) => {
    try {
      await deleteHolidayType.mutateAsync(id);
      setDeleteConfirmId(null);
    } catch (err: any) {
      console.error('Failed to delete holiday type:', err);
      const msg = err?.response?.data?.detail || err?.message || 'Failed to delete holiday type. Please try again.';
      setErrorMessage(msg);
    }
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(orderedItems);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setOrderedItems(items);
    setHasOrderChanged(true);
  };

  const handleSaveOrder = async () => {
    try {
      const orders = orderedItems.map((item, index) => ({
        id: item.id,
        order_index: index,
      }));
      await reorderHolidayTypes.mutateAsync(orders);
      setHasOrderChanged(false);
    } catch (err: any) {
      console.error('Failed to save order:', err);
      const msg = err?.response?.data?.detail || err?.message || 'Failed to save order. Please try again.';
      setErrorMessage(msg);
    }
  };

  // Filter holiday types based on search query
  const filteredHolidayTypes = orderedItems?.filter(type =>
    type.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    type.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Disable reordering if searching
  const isReorderingEnabled = !searchQuery;

  return (
    <>
      <div className="sm:flex sm:items-center sm:justify-between mb-6">
        <div>
          <h3 className="text-lg leading-6 font-medium text-gray-900">Holiday Types</h3>
          <p className="mt-1 text-sm text-gray-500">
            Manage holiday types for your travel offerings
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          {hasOrderChanged && (
            <button
              onClick={handleSaveOrder}
              disabled={reorderHolidayTypes.isPending}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              {reorderHolidayTypes.isPending ? (
                <RefreshCw className="animate-spin -ml-1 mr-2 h-5 w-5" />
              ) : (
                <Save className="-ml-1 mr-2 h-5 w-5" />
              )}
              Save Order
            </button>
          )}
          <Link
            to="/admin/holiday-types/new"
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-teal hover:bg-teal-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal"
          >
            <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add Holiday Type
          </Link>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white shadow rounded-lg p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-grow">
            <label htmlFor="search" className="sr-only">
              Search holiday types
            </label>
            <div className="relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                name="search"
                id="search"
                className="focus:ring-teal focus:border-teal block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                placeholder="Search holiday types"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Holiday Types list */}
      <div className="bg-white shadow rounded-lg">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-charcoal"></div>
            <p className="mt-2">Loading holiday types...</p>
          </div>
        ) : error ? (
          <div className="text-center py-12 text-red-500">
            <p>Error loading holiday types. Please try again later.</p>
          </div>
        ) : filteredHolidayTypes && filteredHolidayTypes.length > 0 ? (
          <div className="overflow-x-auto">
            <DragDropContext onDragEnd={onDragEnd}>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {isReorderingEnabled && <th scope="col" className="w-10"></th>}
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Holiday Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Packages
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <Droppable droppableId="holiday-types" isDropDisabled={!isReorderingEnabled}>
                  {(provided) => (
                    <tbody
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className="bg-white divide-y divide-gray-200"
                    >
                      {filteredHolidayTypes.map((holidayType, index) => (
                        <Draggable
                          key={holidayType.id}
                          draggableId={holidayType.id.toString()}
                          index={index}
                          isDragDisabled={!isReorderingEnabled}
                        >
                          {(provided, snapshot) => (
                            <tr
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={snapshot.isDragging ? 'bg-gray-50' : ''}
                            >
                              {isReorderingEnabled && (
                                <td className="pl-4">
                                  <div {...provided.dragHandleProps} className="text-gray-400 hover:text-gray-600 cursor-grab">
                                    <GripVertical className="h-5 w-5" />
                                  </div>
                                </td>
                              )}
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="flex-shrink-0 h-10 w-10 rounded-md overflow-hidden">
                                    {holidayType.image_id ? (
                                      <CloudflareImage
                                        imageId={holidayType.image_id}
                                        variant="thumbnail"
                                        alt={holidayType.name}
                                        className="h-10 w-10"
                                        objectFit="cover"
                                        placeholder="/home-heros/hero3.webp"
                                      />
                                    ) : (
                                      <img
                                        className="h-10 w-10 object-cover"
                                        src="/home-heros/hero3.webp"
                                        alt={holidayType.name}
                                      />
                                    )}
                                  </div>
                                  <div className="ml-4">
                                    <div className="text-sm font-medium text-gray-900">{holidayType.name}</div>
                                    <div className="text-sm text-gray-500">{holidayType.slug}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {(holidayType as any).package_count || 0} packages
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${holidayType.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                  }}`}>
                                  {holidayType.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <div className="flex items-center justify-end space-x-4">
                                  <Link
                                    to={`/admin/holiday-types/${holidayType.id}/edit`}
                                    className="text-teal hover:text-teal-dark"
                                  >
                                    Edit
                                  </Link>
                                  <Link
                                    to={`/holiday-types/${holidayType.slug}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-gray-600 hover:text-gray-900"
                                  >
                                    View
                                  </Link>
                                  <button
                                    type="button"
                                    onClick={() => setDeleteConfirmId(holidayType.id)}
                                    className="text-red-600 hover:text-red-800"
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </tbody>
                  )}
                </Droppable>
              </table>
            </DragDropContext>
          </div>
        ) : (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No holiday types found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchQuery ? 'No holiday types match your search criteria.' : 'Get started by creating a new holiday type.'}
            </p>
            <div className="mt-6">
              <Link
                to="/admin/holiday-types/new"
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-teal hover:bg-teal-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal"
              >
                <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add Holiday Type
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      {deleteConfirmId !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Confirm Delete</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this holiday type? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => setDeleteConfirmId(null)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                disabled={deleteHolidayType.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDelete(deleteConfirmId)}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                disabled={deleteHolidayType.isPending}
              >
                {deleteHolidayType.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      <ErrorModal
        isOpen={!!errorMessage}
        onClose={() => setErrorMessage(null)}
        title="Action Failed"
        message={errorMessage || ''}
      />
    </>
  );
};

export default HolidayTypesListPage;
